import { useState } from "react";
import { useParams } from "react-router";

import { QueryErrorState } from "@/api/QueryErrorState";
import { useLibraryAssetQuery, type LibraryAsset } from "@/api/library";
import { useCapability } from "@/auth/SessionContext";
import { lifecycleRefusal } from "@/lib/archiveRefusal";
import { formatDateTime } from "@/lib/format";
import { ASSET_TYPE_LABELS, sectionForAssetType } from "@/lib/librarySections";
import { PageHeader } from "@/shell/PageHeader";
import { LIBRARY_SECTIONS, findSection } from "@/shell/navModel";
import { Button } from "@/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";
import { Tooltip } from "@/ui/Tooltip";
import { AlertMatrixAssetEditor } from "@/ui/library/AlertMatrixAssetEditor";
import { AssetAuditTrail } from "@/ui/library/AssetAuditTrail";
import { AssetEditor } from "@/ui/library/AssetEditor";
import { AssetStateBadges } from "@/ui/library/AssetStateBadges";
import {
  LifecycleDialog,
  type LifecycleMode,
} from "@/ui/library/LifecycleDialog";
import { MatrixPinning } from "@/ui/library/MatrixPinning";
import { NewAlertMatrixDialog } from "@/ui/library/NewAlertMatrixDialog";
import {
  NewAssetDialog,
  isCreatableAssetType,
} from "@/ui/library/NewAssetDialog";
import { BackgroundDomainPage } from "@/ui/library/named/BackgroundDomainPage";
import { BackgroundProfilePage } from "@/ui/library/named/BackgroundProfilePage";
import { ClosingConversationPage } from "@/ui/library/named/ClosingConversationPage";
import { MainConcernAssetPage } from "@/ui/library/named/MainConcernAssetPage";
import { PathwayAssetPage } from "@/ui/library/named/PathwayAssetPage";
import { RulesAssetPage } from "@/ui/library/named/RulesAssetPage";
import { SpeechProfilePage } from "@/ui/library/named/SpeechProfilePage";
import { SummaryPromptPage } from "@/ui/library/named/SummaryPromptPage";
import { QuestionnaireInstrument } from "@/ui/library/QuestionnaireInstrument";
import { LoadingState } from "@/ui/states";

/**
 * One shared library asset: provenance, adoption impact, immutable version
 * history, and — since #33 — the authoring surface for its content.
 *
 * Two mutating paths, deliberately distinct. A lifecycle action
 * (archive/unarchive) is catalog curation and sits in the header behind the
 * impact-first dialog. Publishing a version is authoring and sits in the
 * content card with the content it publishes. There is still no delete control
 * anywhere — the contract has no delete route, and archive is the only
 * lifecycle exit.
 *
 * Two authoring surfaces and one read-only one, chosen by the asset's CONTENT
 * shape rather than by its section. Parts-shaped assets — including the safety
 * track since #35 — take #33's `AssetEditor`; the summary prompt did too from
 * #442 until #863 gave it a named surface of its own. An alert matrix is typed
 * content with its own invariants and takes #35's `AlertMatrixAssetEditor`. Each of those two has a create
 * destination for the content shape it authors, so a seeded asset's "copy it
 * into a new asset" refusal is an instruction rather than a dead end — the
 * matrix's arrived last, in #494.
 *
 * A SPEECH PROFILE LEFT THIS PAGE IN #870, and the branch is gone rather than
 * merely unreachable. #869 rendered its two typed documents here through
 * `VersionContent` with a stated read-only reason, because the generic parts
 * form offers free-form part keys and raw JSON over them — the arbitrary file
 * editor the ratified screen contract forbids, and worst of all on a document
 * of two hundred Hebrew pairs with combining vowel marks. `SpeechProfilePage`
 * is the named editor that comment named, and `NewSpeechProfileDialog` is the
 * create and copy destination it was missing.
 *
 * A QUESTIONNAIRE HAS NO AUTHORING SURFACE since #638, and no copy destination.
 * It is typed content and had both (#368), on the premise that the seeded
 * refusal's instruction needed a destination. Ratified §2.2 closed that
 * premise: the family is governed, its approved inventory is seeded from the
 * clinical catalog, and both authoring routes refuse it — so a copy would be an
 * instrument nobody licensed. `QuestionnaireInstrument` is what the branch
 * renders instead: the provenance, the reason authoring is unavailable, and the
 * instrument read-only. That is not the retired `ReadOnlyAssetContent` fallback
 * returning; it is a real branch for a family whose answer is genuinely
 * read-only, rather than one still owed an editor.
 *
 * THERE WAS A FOURTH until #442, and its removal is the point rather than a
 * tidy-up. `ReadOnlyAssetContent` rendered the head of any type with no editor
 * yet, above a notice naming the epic that owed one. `summary_prompt` was the
 * last such type — it has since moved on to its own named page (#863), but the
 * fallback it retired stays retired —
 * and an unreachable branch that renders a surface is exactly the dead control
 * the shell's invariants forbid. What replaces it is `everyAssetTypeIsRouted`
 * below, which makes the same claim to the compiler instead of to a reader.
 *
 * An alert matrix additionally carries the project-pinning surface, because the
 * adoption endpoints it needs have been complete since #28 while the project's
 * own `safety`/`alerts` sections remain gated on #29 G3.
 */
export function LibraryAssetRoute() {
  const { assetId = "" } = useParams();
  const asset = useLibraryAssetQuery(assetId);

  if (asset.isPending) {
    return <LoadingState label="Loading library asset" rows={4} />;
  }

  if (asset.isError) {
    return (
      <QueryErrorState
        error={asset.error}
        subject="Library asset"
        title="This library asset could not be loaded"
        description="The Editor Service did not return this asset."
        deniedDescription="You are not authorized to read this asset, or it does not exist in your scope."
        // Asset ids are opaque: "absent" and "not yours" are deliberately
        // indistinguishable, per the contract's 404 wording.
        treatNotFoundAsDenied
        onRetry={() => void asset.refetch()}
      />
    );
  }

  return <AssetPage asset={asset.data} />;
}

/**
 * The compile-time floor under the dispatch below.
 *
 * Every one of the contract's nine asset types reaches a real surface: the six
 * in `CREATABLE_ASSET_TYPES`, the alert matrix, and the questionnaire — the
 * independent BG set left this dispatch in #859 for its own named page, which
 * is why its branch is gone rather than merely unreachable. So `assetType` is
 * `never` by the time it arrives here, and a TENTH type in a
 * regenerated contract widens it and fails THIS call rather than silently
 * rendering as a questionnaire. It is the same guard `librarySections.ts` puts
 * under the section map with `satisfies`, and it is what lets #442 delete the
 * read-only fallback without replacing it with an else-branch that quietly
 * mis-edits the next type somebody adds.
 */
function everyAssetTypeIsRouted(_assetType: never): null {
  return null;
}

function AssetPage({ asset }: { asset: LibraryAsset }) {
  // #492: main concern's authoring surface is the canonical NAMED document one,
  // so it owns the whole page, header included, and is dispatched before the
  // generic chrome below is assembled.
  //
  // This is not a preference about layout. `AssetEditor` reaches `PartsEditor`,
  // whose free-text "Part key" input is precisely the manual-key escape hatch
  // the 2026-08-25 canonical amendment forbids for main concern. Every other
  // authorable parts-shaped family keeps it, unchanged.
  if (asset.assetType === "main_concern") {
    return <MainConcernAssetPage asset={asset} />;
  }
  // #643: pathways take the named STEP authoring surface for the same reason
  // main concern takes the named document one — `AssetEditor` reaches
  // `PartsEditor`, whose free-text "Part key" input is the manual-key escape
  // hatch the 2026-08-25 amendment forbids and whose structured field is a
  // writable textarea over raw JSON, which the ratified product contract lists
  // among the rejected mechanisms for this family. Both types go to one page:
  // they are one clinical family, split only for adoption identity.
  if (asset.assetType === "pathway" || asset.assetType === "pathway_variant") {
    return <PathwayAssetPage asset={asset} />;
  }
  // #859: a background set takes the named DOMAIN surface, on the same rule and
  // for a premise that #857 changed. `AssetEditor` reaches `PartsEditor`, whose
  // structured field is a writable textarea over raw JSON — which was the only
  // honest option while `StructuredPart.data` was an unconstrained `JsonValue`
  // with no published shape. `background_domains.py` publishes that shape now,
  // as a write-time schema pinned to the runtime tracker's own vocabulary, so a
  // typed surface renders the service's contract rather than inventing one.
  if (asset.assetType === "background_set") {
    return <BackgroundDomainPage asset={asset} />;
  }
  // #914: a shared BG interview profile takes the named PROFILE surface, and
  // this branch replaces the one #858 left here deliberately. That branch sent
  // the family to `AssetEditor` and said why: the family and its typed tone
  // document existed on the service, but "the named editing surface the Lovable
  // reference shows is `bg_profile_ui`'s (#914)". This is that surface — the
  // reference's `Set files` rows over one profile's named parts, the large
  // prompt field behind two of them, and typed named controls behind the tone
  // document that `PartsEditor` could only have offered as raw JSON.
  if (asset.assetType === "background_profile") {
    return <BackgroundProfilePage asset={asset} />;
  }
  // #863: a Summary prompt takes the named PROMPT surface, for the reason every
  // family above takes theirs — `AssetEditor` reaches `PartsEditor`, whose
  // free-text "Part key" input is the manual-key escape hatch the 2026-08-25
  // amendment forbids and whose structured field is a writable textarea over
  // raw JSON. It also carries the one fact the generic page cannot state: WHICH
  // Summary prompt this is, and the exact pack file that target writes.
  if (asset.assetType === "summary_prompt") {
    return <SummaryPromptPage asset={asset} />;
  }
  // #910: Rules take the named LANGUAGE surface, for the two reasons the
  // families above take theirs and one of their own. `AssetEditor` reaches
  // `PartsEditor`, whose free-text "Part key" input is the manual-key escape
  // hatch the 2026-08-25 amendment forbids and whose structured field is a
  // writable textarea over raw JSON. The third is this family's alone: a Rule's
  // `substitutions` document DEEP-MERGES onto base canon's, so the raw part is
  // not what the runtime applies — a clinician editing it saw the handful of
  // entries this asset overrides and none of the hundreds it inherits.
  if (asset.assetType === "language_rules") {
    return <RulesAssetPage asset={asset} />;
  }
  // #867: a closing conversation takes the named PROMPT + ORDERED STEP surface,
  // for the reason every family above takes theirs — `AssetEditor` reaches
  // `PartsEditor`, whose free-text "Part key" input is the manual-key escape
  // hatch the 2026-08-25 amendment forbids and whose structured field is a
  // writable textarea over raw JSON. #866 published the shape behind that JSON
  // (`closing_conversation.py`: ordered `{id, instruction}` steps, unique
  // integral ids, and a completion instruction), which is exactly the authority
  // this dispatch was waiting on, and named this leaf as the replacement.
  if (asset.assetType === "closing_conversation") {
    return <ClosingConversationPage asset={asset} />;
  }
  // #870: a speech profile takes the named SPEECH surface. It was read-only
  // here until this ticket — #869 rendered its content through `VersionContent`
  // with a stated reason, because the generic parts form offers free-form part
  // keys and raw JSON over two typed documents, and a two-hundred-entry Hebrew
  // table with combining vowel marks is the case where that is worst. The named
  // editor is the destination that comment named.
  if (asset.assetType === "speech_profile") {
    return <SpeechProfilePage asset={asset} />;
  }
  return <GenericAssetPage asset={asset} />;
}

/**
 * The asset types that reach the generic page — every one the dispatch above
 * does NOT claim.
 *
 * DERIVED, never listed: `Exclude` is what keeps `everyAssetTypeIsRouted`'s
 * exhaustiveness claim true. Naming the remaining six by hand would make a
 * TENTH contract type default into this page silently, which is the failure
 * that guard exists to prevent.
 */
type GenericAssetType = Exclude<
  LibraryAsset["assetType"],
  | "main_concern"
  | "pathway"
  | "pathway_variant"
  | "background_set"
  | "closing_conversation"
  | "background_profile"
  | "speech_profile"
>;

function GenericAssetPage({
  asset,
}: {
  asset: Extract<LibraryAsset, { assetType: GenericAssetType }>;
}) {
  const write = useCapability("library.write");
  const restore = useCapability("library.restore");
  const [lifecycle, setLifecycle] = useState<LifecycleMode | null>(null);
  const [copying, setCopying] = useState(false);
  const [copyingMatrix, setCopyingMatrix] = useState(false);

  // The canonical section comes from the loaded asset, not the URL segment:
  // the id is authoritative and a stale link must not relabel the asset.
  const section = sectionForAssetType(asset.assetType);
  const sectionLabel = findSection(LIBRARY_SECTIONS, section)?.label ?? section;

  const mode: LifecycleMode = asset.capabilities.archived
    ? "unarchive"
    : "archive";
  // The SAME derivation the catalog rows use — one shared function since #214,
  // because two copies of the policy is what the ticket removed. The caller
  // half depends on the DIRECTION (#647): archiving is `library.write`,
  // restoring is the admin/operations `library.restore` the ratified contract
  // (§2.1) reserves, and the service gates the two routes the same way.
  const refusal = lifecycleRefusal(
    asset,
    (mode === "unarchive" ? restore.reason : write.reason) ?? null,
  );
  const lifecycleLabel = mode === "archive" ? "Archive" : "Unarchive";

  return (
    <>
      <PageHeader
        breadcrumbs={[
          // #489: the shared catalogue is the "Clinical library". "Clinical
          // editor" named the whole application, so this crumb read as a link
          // out of the product rather than up to the catalogue.
          { label: "Clinical library", to: "/library" },
          { label: sectionLabel, to: `/library/${section}` },
        ]}
        title={asset.name}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <span>{ASSET_TYPE_LABELS[asset.assetType]}</span>
            <AssetStateBadges capabilities={asset.capabilities} />
            {/*
              THE IMMUTABLE PACK PLACEMENT, read-only, as the service states it:
              an authored row's stored coordinate, or — for base canon, whose
              placement is the seed manifest's — the coordinate the server
              derives from it. A clinician copying this asset to replace it
              needs this coordinate; before it was served, the copy dialog's
              blank field left them guessing an invisible one.
            */}
            {asset.placementCoordinate ? (
              <span className="text-muted-foreground">
                Pack placement{" "}
                <code
                  dir="ltr"
                  className="rounded bg-muted px-1 py-0.5 font-mono text-xs text-foreground"
                >
                  {asset.placementCoordinate}
                </code>
              </span>
            ) : null}
          </span>
        }
        meta={`${asset.version} · Updated ${formatDateTime(asset.updatedAt)} · Created by ${asset.createdBy}`}
        actions={
          <Tooltip content={refusal ?? `${lifecycleLabel} this asset`}>
            <Button
              variant="outline"
              disabled={refusal !== null}
              onClick={() => setLifecycle(mode)}
            >
              {lifecycleLabel}
            </Button>
          </Tooltip>
        }
      />

      <div className="space-y-6">
        {isCreatableAssetType(asset.assetType) ? (
          <AssetEditor asset={asset} onCopy={() => setCopying(true)} />
        ) : asset.assetType === "alert_matrix" ? (
          // The copy has a destination since #494. It was `null` because
          // `NewAssetDialog` authors parts and a matrix is typed content, so a
          // Copy opening a parts form would have been the dead control the
          // shell's invariants forbid — and this branch's comment named the
          // follow-up that would close it. `NewAlertMatrixDialog` is that
          // destination; the copy button itself has been in the editor's
          // read-only branch since #35, waiting for this handler.
          <AlertMatrixAssetEditor
            asset={asset}
            onCopy={() => setCopyingMatrix(true)}
          />
        ) : asset.assetType === "questionnaire" ? (
          // NO COPY DESTINATION, and no authoring surface (#638). #368 opened
          // this seam on the premise that the service's refusal for seeded
          // canon — "copy it into a new asset or override per project" — needed
          // somewhere to go; ratified §2.2 closed that premise. The family is
          // governed, both authoring routes refuse it, and a copy would be an
          // unlicensed instrument under a trusted name. `QuestionnaireInstrument`
          // states the provenance and why authoring is unavailable, and renders
          // the instrument read-only.
          <QuestionnaireInstrument asset={asset} />
        ) : (
          everyAssetTypeIsRouted(asset.assetType)
        )}

        {asset.assetType === "alert_matrix" ? (
          <Card>
            <CardHeader>
              <CardTitle>Project pinning</CardTitle>
            </CardHeader>
            <CardBody>
              <MatrixPinning asset={asset} />
            </CardBody>
          </Card>
        ) : null}

        <AssetAuditTrail assetId={asset.id} />
      </div>

      {lifecycle ? (
        <LifecycleDialog
          asset={asset}
          mode={lifecycle}
          onClose={() => setLifecycle(null)}
        />
      ) : null}

      {copying && isCreatableAssetType(asset.assetType) ? (
        <NewAssetDialog copyFrom={asset} onClose={() => setCopying(false)} />
      ) : null}

      {copyingMatrix && asset.assetType === "alert_matrix" ? (
        <NewAlertMatrixDialog
          copyFrom={asset}
          onClose={() => setCopyingMatrix(false)}
        />
      ) : null}
    </>
  );
}
